import { Card, Link, Stack, Typography } from '@mui/material';
import { useAtom } from 'jotai';

import DeviceStatusChip from '@/components/DeviceStatusChip';
import { userIsInternalAtom } from '@/store/user.store';

import {
  calculateStatusFromStatusCode,
  humanizeTime
} from '../../Devices.service';
import type { DeviceStatusCardProps } from './DeviceStatusCard.types';

/**
 * Renders a single device status card with optional internal-only deep links.
 *
 * @param props Component properties.
 * @returns Device status card view.
 */
export const DeviceStatusCard = ({ status }: DeviceStatusCardProps) => {
  const { payload, status_code } = status || {};
  const [userIsInternal] = useAtom(userIsInternalAtom);

  const statusText = calculateStatusFromStatusCode(`${status_code}`);
  const label = status?.status_code_detail?.status_label;

  const hasStatusLink =
    !!payload?.link &&
    userIsInternal &&
    !/scope([a-z-]+)?.pondurance.com/.test(payload.link);

  return (
    <Card variant="outlined" sx={{ px: 2, py: 2 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        spacing={1}
      >
        <DeviceStatusChip status={statusText} />
        <Typography variant="body2" color="textSecondary">
          {humanizeTime(status?.status_updated_at || '', true)}
        </Typography>
      </Stack>
      <Typography
        sx={{ fontWeight: 'bold', py: 1 }}
        variant="body1"
        component="div"
      >
        {/* links point to tools like datadog, nomad or terraform and only visible to internal users */}
        {hasStatusLink ? (
          <Link href={payload?.link || ''} target="_blank" underline="none">
            {status_code} - {label}
          </Link>
        ) : (
          <span>
            {status_code} - {label}
          </span> // Fallback if no link is available
        )}
      </Typography>
      <Typography variant="body1">{payload?.title}</Typography>
    </Card>
  );
};

export default DeviceStatusCard;
